import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import type { EventSetting } from "../../lib/api";
import {
  ActionMessage,
  Button,
  Field,
  Input,
  MoneyInput,
  PanelSection,
  Select,
  useAdminAction,
} from "./shared";

type FormState = Omit<EventSetting, "id" | "updatedAt" | "scheduledAt"> & { scheduledAt: string };

function toForm(e: EventSetting): FormState {
  return {
    ...e,
    scheduledAt: e.scheduledAt ? e.scheduledAt.slice(0, 16) : "",
  };
}

export default function EventPanel({ event, onSaved }: { event: EventSetting; onSaved: () => void }) {
  const [form, setForm] = useState<FormState>(toForm(event));
  const { message, busy, run } = useAdminAction();

  useEffect(() => {
    setForm(toForm(event));
  }, [event]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const save = () =>
    run(async () => {
      await api.adminSend("/event-settings", "PUT", {
        eventName: form.eventName,
        wordmark: form.wordmark,
        description: form.description,
        scheduledAt: form.scheduledAt ? new Date(form.scheduledAt).toISOString() : null,
        venue: form.venue || null,
        venueName: form.venueName || null,
        venueMapUrl: form.venueMapUrl || null,
        venueAddress: form.venueAddress || null,
        venueFloor: form.venueFloor || null,
        venueStatus: form.venueStatus,
        venueBookingNote: form.venueBookingNote,
        timingNote: form.timingNote,
        lunchTime: form.lunchTime || null,
        status: form.status,
        totalCohort: Number(form.totalCohort),
        seniorsCount: Number(form.seniorsCount),
        juniorsCount: Number(form.juniorsCount),
        expectedAttendees: Number(form.expectedAttendees),
        participationRateBps: Number(form.participationRateBps),
        contributionPaise: Number(form.contributionPaise),
        seniorContributionPaise: Number(form.seniorContributionPaise),
        juniorContributionPaise: Number(form.juniorContributionPaise),
        contingencyRateBps: Number(form.contingencyRateBps),
        transportSeats: Number(form.transportSeats),
        contributorCount: Number(form.contributorCount),
        actualAttendees: Number(form.actualAttendees),
        actualSeniorAttendees: Number(form.actualSeniorAttendees),
        actualJuniorAttendees: Number(form.actualJuniorAttendees),
        surplusPolicy: form.surplusPolicy,
        perPersonExpensePaise: form.perPersonExpensePaise,
        surplusCustomCount: Number(form.surplusCustomCount),
        videoUrl: form.videoUrl,
      });
      onSaved();
    }, "Event settings saved.");

  return (
    <PanelSection
      title="Event identity & assumptions"
      description="All figures on the public site — cohort sizes, expected attendance, participation assumption, and surplus policy — come from these fields. Provisional values are clearly labelled as estimates."
      action={<Button onClick={save} loading={busy}>Save settings</Button>}
    >
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <Field label="Event name" htmlFor="ev-name">
          <Input id="ev-name" value={form.eventName} onChange={(e) => set("eventName", e.target.value)} maxLength={120} />
        </Field>
        <Field label="Wordmark (nav & footer)" htmlFor="ev-wordmark">
          <Input id="ev-wordmark" value={form.wordmark} onChange={(e) => set("wordmark", e.target.value)} maxLength={60} />
        </Field>
        <div className="md:col-span-2">
          <Field label="Description" hint="Shown under the overview heading.">
            <textarea
              className="w-full min-h-[96px] rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2.5 text-sm outline-none focus:border-wandor-prompt"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              maxLength={4000}
            />
          </Field>
        </div>
        <Field label="Event date & time" hint="Leave empty to show “To be decided”." htmlFor="ev-date">
          <Input id="ev-date" type="datetime-local" value={form.scheduledAt} onChange={(e) => set("scheduledAt", e.target.value)} />
        </Field>
        <Field label="Venue (legacy single-line)" hint="Superseded by the venue fields below." htmlFor="ev-venue">
          <Input id="ev-venue" value={form.venue ?? ""} onChange={(e) => set("venue", e.target.value)} maxLength={240} />
        </Field>
        <Field label="Venue / banquet-hall name" hint="Shown publicly as confirmed only when venue status = confirmed." htmlFor="ev-venue-name">
          <Input id="ev-venue-name" value={form.venueName ?? ""} onChange={(e) => set("venueName", e.target.value)} maxLength={240} />
        </Field>
        <Field label="Venue floor" hint="e.g. “1st floor banquet hall”. Public site shows “Floor details awaiting confirmation” when empty." htmlFor="ev-venue-floor">
          <Input id="ev-venue-floor" value={form.venueFloor ?? ""} onChange={(e) => set("venueFloor", e.target.value)} maxLength={120} />
        </Field>
        <Field label="Venue address" htmlFor="ev-venue-address">
          <Input id="ev-venue-address" value={form.venueAddress ?? ""} onChange={(e) => set("venueAddress", e.target.value)} maxLength={500} />
        </Field>
        <Field label="Venue map link" hint="Google Maps (or other) link shown as a directions button." htmlFor="ev-venue-map">
          <Input id="ev-venue-map" value={form.venueMapUrl ?? ""} onChange={(e) => set("venueMapUrl", e.target.value)} maxLength={500} />
        </Field>
        <Field label="Venue status" htmlFor="ev-venue-status">
          <Select
            id="ev-venue-status"
            value={form.venueStatus}
            onChange={(e) => set("venueStatus", e.target.value as FormState["venueStatus"])}
          >
            <option value="awaiting">Awaiting confirmation</option>
            <option value="under_consideration">Under consideration</option>
            <option value="confirmed">Confirmed</option>
          </Select>
        </Field>
        <Field label="Lunch serving / start time" hint="Leave empty to show “Serving time awaiting confirmation”." htmlFor="ev-lunch-time">
          <Input id="ev-lunch-time" value={form.lunchTime ?? ""} onChange={(e) => set("lunchTime", e.target.value)} maxLength={120} />
        </Field>
        <Field label="Expected timing" htmlFor="ev-timing-note">
          <Input id="ev-timing-note" value={form.timingNote} onChange={(e) => set("timingNote", e.target.value)} maxLength={120} />
        </Field>
        <div className="md:col-span-2">
          <Field label="Booking details (duration, capacity, advance, payment terms)" hint="Public until shown; leave empty for “awaiting quotation” notice.">
            <textarea
              className="w-full min-h-[72px] rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2.5 text-sm text-white outline-none focus:border-wandor-prompt"
              value={form.venueBookingNote}
              onChange={(e) => set("venueBookingNote", e.target.value)}
              maxLength={2000}
            />
          </Field>
        </div>
        <Field label="Event status" htmlFor="ev-status">
          <Select id="ev-status" value={form.status} onChange={(e) => set("status", e.target.value)}>
            <option value="planning">Planning</option>
            <option value="voting_open">Voting open</option>
            <option value="finalized">Finalized</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </Select>
        </Field>
        <Field label="Hero background video URL" hint="If the video fails to load, a static gradient fallback is shown." htmlFor="ev-video">
          <Input id="ev-video" value={form.videoUrl} onChange={(e) => set("videoUrl", e.target.value)} maxLength={2000} />
        </Field>
      </div>

      <h3 className="mt-8 text-lg font-semibold text-white">Cohort & attendance estimates</h3>
      <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Total cohort" htmlFor="ev-total">
          <Input id="ev-total" type="number" min={1} value={form.totalCohort} onChange={(e) => set("totalCohort", Number(e.target.value))} />
        </Field>
        <Field label="Second-year seniors" htmlFor="ev-seniors">
          <Input id="ev-seniors" type="number" min={0} value={form.seniorsCount} onChange={(e) => set("seniorsCount", Number(e.target.value))} />
        </Field>
        <Field label="First-year juniors" htmlFor="ev-juniors">
          <Input id="ev-juniors" type="number" min={0} value={form.juniorsCount} onChange={(e) => set("juniorsCount", Number(e.target.value))} />
        </Field>
        <Field label="Expected attendees" htmlFor="ev-expected">
          <Input id="ev-expected" type="number" min={0} value={form.expectedAttendees} onChange={(e) => set("expectedAttendees", Number(e.target.value))} />
        </Field>
        <Field label="Participation rate (%)" hint="Stored in basis points (50% = 5000)." htmlFor="ev-rate">
          <Input
            id="ev-rate"
            type="number"
            min={0}
            max={100}
            value={form.participationRateBps / 100}
            onChange={(e) => set("participationRateBps", Math.round(Number(e.target.value) * 100))}
          />
        </Field>
        <Field label="Estimated transport passengers" htmlFor="ev-seats">
          <Input id="ev-seats" type="number" min={0} value={form.transportSeats} onChange={(e) => set("transportSeats", Number(e.target.value))} />
        </Field>
        <MoneyInput
          label="Contribution assumption (₹ / student)"
          paise={form.contributionPaise}
          onChangePaise={(p) => set("contributionPaise", p ?? 0)}
          hint="Planning assumption used in funding-gap maths."
          id="ev-contribution"
        />
        <MoneyInput
          label="Senior contribution (₹ / senior)"
          paise={form.seniorContributionPaise}
          onChangePaise={(p) => set("seniorContributionPaise", p ?? 0)}
          hint="0 = awaiting confirmation. Shown on the cost-combination cards."
          id="ev-senior-contribution"
        />
        <MoneyInput
          label="Junior contribution (₹ / junior)"
          paise={form.juniorContributionPaise}
          onChangePaise={(p) => set("juniorContributionPaise", p ?? 0)}
          hint="Organizer-confirmed: ₹790. Part of the ₹2,080 unit pair."
          id="ev-junior-contribution"
        />
        <MoneyInput
          label="Per-person expenditure (₹ / attendee)"
          paise={form.perPersonExpensePaise}
          onChangePaise={(p) => set("perPersonExpensePaise", p ?? 0)}
          hint="Public planning figure shown on the site (organizer: ₹1,000 — dinner + DJ + decoration + other amenities)."
          id="ev-per-person-expense"
        />
        <Field label="Actual senior attendees" hint="Editable after the event — never inferred from votes." htmlFor="ev-actual-seniors">
          <Input id="ev-actual-seniors" type="number" min={0} value={form.actualSeniorAttendees} onChange={(e) => set("actualSeniorAttendees", Number(e.target.value))} />
        </Field>
        <Field label="Actual junior attendees" hint="Editable after the event." htmlFor="ev-actual-juniors">
          <Input id="ev-actual-juniors" type="number" min={0} value={form.actualJuniorAttendees} onChange={(e) => set("actualJuniorAttendees", Number(e.target.value))} />
        </Field>
        <Field label="Contingency rate (%)" htmlFor="ev-contingency">
          <Input
            id="ev-contingency"
            type="number"
            min={0}
            max={100}
            value={form.contingencyRateBps / 100}
            onChange={(e) => set("contingencyRateBps", Math.round(Number(e.target.value) * 100))}
          />
        </Field>
      </div>

      <h3 className="mt-8 text-lg font-semibold text-white">Surplus distribution policy</h3>
      <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-3">
        <Field label="Eligible recipients" htmlFor="ev-policy">
          <Select id="ev-policy" value={form.surplusPolicy} onChange={(e) => set("surplusPolicy", e.target.value)}>
            <option value="cohort">Entire enrolled cohort (seniors + juniors)</option>
            <option value="contributors">Contributing students only</option>
            <option value="attendees">Actual attendees only</option>
            <option value="custom">Custom confirmed count</option>
                      <option value="share_ratio">Share ratio — refund by contribution share (₹1,290 : ₹790)</option>
          </Select>
        </Field>
        <Field label="Contributor count" hint="Used by the “contributors only” policy." htmlFor="ev-contributors">
          <Input id="ev-contributors" type="number" min={0} value={form.contributorCount} onChange={(e) => set("contributorCount", Number(e.target.value))} />
        </Field>
        <Field label="Actual attendees" hint="Used by the “attendees only” policy." htmlFor="ev-actual">
          <Input id="ev-actual" type="number" min={0} value={form.actualAttendees} onChange={(e) => set("actualAttendees", Number(e.target.value))} />
        </Field>
        <Field label="Custom eligible count" hint="Used by the “custom” policy." htmlFor="ev-custom">
          <Input id="ev-custom" type="number" min={0} value={form.surplusCustomCount} onChange={(e) => set("surplusCustomCount", Number(e.target.value))} />
        </Field>
      </div>

      <ActionMessage message={message} />
    </PanelSection>
  );
}
